import { quoteIdentFor, makePlaceholder } from '../extensions/dbEngines';
import { sqlLiteral } from './dbExportUtils';
import { isOpaqueCell, isUndecoded } from './dbCellTags';

/**
 * Pure SQL statement builders for the DB Manager grid's write path — the
 * UPDATE/INSERT/DELETE batches DbManagerResultsTab hands to the per-engine
 * `*_execute_batch` commands. Kept free of React/Tauri so they can be
 * exercised in a plain node harness (same deal as dbExportUtils).
 *
 * Why postgres placeholders carry casts: the grid stages every cell value as
 * a string (they come out of <input>s/textareas), and the Rust side binds
 * JsonValue::String as a TEXT parameter. Postgres's extended protocol does
 * NOT coerce text→integer/numeric/boolean/… for bound params (MySQL does,
 * SQLite has type affinity — so only postgres breaks), so a staged "1"
 * against an `id integer` column fails with: column "id" is of type integer
 * but expression is of type text. Every placeholder the postgres path
 * generates therefore gets an explicit cast derived from the column's
 * information_schema metadata: `"id" = $1::integer`. NULL params need no
 * special-casing — the driver binds a text NULL and `NULL::integer` casts
 * fine.
 */

// information_schema.columns data_type values that are safe to cast to
// verbatim. Type names with spaces ("timestamp with time zone", "double
// precision") are valid in `::` casts without quoting. Text-ish types
// (text, character varying, character) are omitted on purpose: the bound
// param is already TEXT, so a cast would be a no-op.
const PG_CAST_TYPES = new Set([
  'integer',
  'smallint',
  'bigint',
  'numeric',
  'real',
  'double precision',
  'boolean',
  'date',
  'time without time zone',
  'timestamp without time zone',
  'timestamp with time zone',
  'uuid',
  'json',
  'jsonb',
  'bytea',
]);

/**
 * `::<type>` cast suffix for a postgres column's metadata ({ name, type,
 * udtName, udtSchema }, type = information_schema data_type), or '' when no
 * safe cast exists. 'USER-DEFINED' (enums and friends) casts to the quoted
 * udt_name, schema-qualified when udt_schema is known — a bare type name
 * only resolves inside the connection's search_path, and grid tables can
 * live in (and reference types from) any schema. 'ARRAY' and anything
 * unrecognized stay uncast — TEXT either already works or we can't name the
 * type safely.
 */
export function pgCastFor(colMeta) {
  if (!colMeta) return '';
  const dataType = String(colMeta.type || '');
  if (dataType === 'USER-DEFINED') {
    const udt = colMeta.udtName;
    if (!udt) return '';
    const quote = (ident) => `"${String(ident).replace(/"/g, '""')}"`;
    const schema = colMeta.udtSchema;
    return schema ? `::${quote(schema)}.${quote(udt)}` : `::${quote(udt)}`;
  }
  const lower = dataType.toLowerCase();
  return PG_CAST_TYPES.has(lower) ? `::${lower}` : '';
}

/**
 * (columnName) => cast suffix, from the engine + the tab's discovered column
 * metadata. MySQL/SQLite coerce text params themselves, so for them (and
 * while postgres metadata hasn't loaded yet) this is a constant ''.
 */
export function makeColumnCaster(engine, tableColumns) {
  if (engine !== 'postgres' || !Array.isArray(tableColumns) || tableColumns.length === 0) {
    return () => '';
  }
  const byName = new Map(tableColumns.map((c) => [c.name, c]));
  return (name) => pgCastFor(byName.get(name));
}

/**
 * WHERE fragment locating the row a foreign key points AT, for the grid's
 * filter box: `"id" = 'ada'`, AND-joined for a composite key.
 *
 * A literal rather than a bound parameter because the filter box is a verbatim
 * SQL surface — runTableQuery concatenates its contents and binds nothing. That
 * makes the quoting load-bearing, so it reuses the same engine-aware helpers
 * the export path does: identifiers through quoteIdentFor, values through
 * sqlLiteral (which doubles quotes, and backslashes on MySQL). Nothing the user
 * typed reaches it: both sides come from catalog metadata and cell values.
 *
 * Null when any member of the key is NULL or opaque — there is no row to
 * navigate to, and `= NULL` would match nothing anyway.
 */
export function buildFkFilterFragment({ engine, fk, values }) {
  if (!fk || !Array.isArray(fk.refColumns) || fk.refColumns.length === 0) return null;
  if (!Array.isArray(values) || values.length !== fk.refColumns.length) return null;
  const parts = [];
  for (let i = 0; i < fk.refColumns.length; i += 1) {
    const v = values[i];
    if (v === null || v === undefined || isOpaqueCell(v)) return null;
    parts.push(`${quoteIdentFor(engine, fk.refColumns[i])} = ${sqlLiteral(engine, v)}`);
  }
  return parts.join(' AND ');
}

/**
 * The FK dictionary's lookup: key + label columns from the referenced table,
 * optionally narrowed by a search term.
 *
 * Bound parameters, not literals — unlike the filter fragment above, the search
 * term IS user input, so it goes through the prepared path. LIKE metacharacters
 * in it are escaped so typing `%` searches for a percent sign rather than
 * matching everything.
 *
 * Always LIMIT-ed and always searchable, rather than HeidiSQL's approach of
 * only offering a dropdown when the referenced table is under a row threshold:
 * a bounded, searchable list works the same on ten rows as on ten million.
 */
export function buildDictionaryQuery({ engine, tableRef, keyColumn, displayColumns, search, limit = 200 }) {
  const cols = [keyColumn, ...displayColumns.filter((c) => c !== keyColumn)];
  const select = cols.map((c) => quoteIdentFor(engine, c)).join(', ');
  const labelCol = quoteIdentFor(engine, displayColumns[0] || keyColumn);
  const term = String(search || '').trim();

  let where = '';
  const params = [];
  if (term) {
    const escaped = term.replace(/[\\%_]/g, '\\$&');
    if (engine === 'postgres') {
      // ::text so a non-text label column can still be searched; ILIKE because
      // postgres LIKE is case-sensitive while the other two are not by default.
      where = ` WHERE ${labelCol}::text ILIKE $1`;
    } else {
      where = ` WHERE CAST(${labelCol} AS CHAR) LIKE ?`;
    }
    params.push(`%${escaped}%`);
  }
  // The escape character is SQL text even though the search term is bound, so
  // it needs the same treatment as the filter fragment below: writing
  // `ESCAPE '\'` literally is valid on postgres/sqlite but not on MySQL, where
  // the backslash escapes the closing quote and the statement never ends.
  const escapeClause = term ? ` ESCAPE ${sqlLiteral(engine, '\\')}` : '';
  return {
    sql: `SELECT ${select} FROM ${tableRef}${where}${escapeClause} ORDER BY ${labelCol} LIMIT ${Number(limit) || 200}`,
    params,
  };
}

/**
 * The grid's WHERE box, cleaned up enough to concatenate.
 *
 * The text goes into `SELECT * FROM t WHERE <this> ORDER BY … LIMIT n`, so a
 * trailing semicolon — which is how most people end a SQL statement, and which
 * the box invites by looking like a SQL editor — lands in the middle and the
 * engine reports a syntax error at LIMIT. Strip it rather than explaining it.
 *
 * ONLY trailing ones. A semicolon in the middle is left exactly where it is:
 * the table-browse path uses the prepared, single-statement protocol precisely
 * so `1=1; DROP TABLE users` typed here is REJECTED by the driver rather than
 * run, and quietly removing the separator would hand that back.
 */
export function normalizeWhereFragment(text) {
  let out = String(text ?? '').trim();
  // A loop, so `id = 3;;` and `id = 3; ;` both come out clean.
  while (out.endsWith(';')) out = out.slice(0, -1).trimEnd();
  return out;
}

/**
 * WHERE fragment for the grid's filter box, built from a cell the user
 * right-clicked: `"status" = 'active'`, `"name" LIKE '%ada%'`, `"id" IN (…)`.
 *
 * A literal rather than a bound parameter for the same reason as
 * buildFkFilterFragment — the filter box is concatenated verbatim and binds
 * nothing — so the quoting is the safety, and it comes from the same
 * engine-aware helpers the export path uses.
 *
 * `op` is one of: eq, ne, like, in, null, notnull.
 */
export function buildFilterFragment(engine, column, op, values = []) {
  const ident = quoteIdentFor(engine, column);
  if (op === 'null') return `${ident} IS NULL`;
  if (op === 'notnull') return `${ident} IS NOT NULL`;

  const usable = values.filter((v) => v !== null && v !== undefined && !isOpaqueCell(v));
  if (usable.length === 0) return null;

  if (op === 'in') {
    // Deduped on the rendered literal so 1 and '1' do not both appear.
    const seen = new Set();
    const literals = [];
    for (const v of usable) {
      const lit = sqlLiteral(engine, v);
      if (!seen.has(lit)) {
        seen.add(lit);
        literals.push(lit);
      }
    }
    return `${ident} IN (${literals.join(', ')})`;
  }

  const v = usable[0];
  if (op === 'like') {
    // Escape the wildcards so searching for "50%" finds a percent sign rather
    // than matching everything. ESCAPE is explicit because SQLite has no
    // default escape character.
    const escaped = String(v).replace(/[\\%_]/g, '\\$&');
    // The escape character goes through sqlLiteral too. Writing `ESCAPE '\'`
    // directly is valid on Postgres/SQLite but not on MySQL, where the
    // backslash escapes the closing quote and the statement never terminates.
    return `${ident} LIKE ${sqlLiteral(engine, `%${escaped}%`)} ESCAPE ${sqlLiteral(engine, '\\')}`;
  }
  return `${ident} ${op === 'ne' ? '<>' : '='} ${sqlLiteral(engine, v)}`;
}

// Builds the WHERE clause + params identifying `result.rows[rowIndex]` in the
// database, preferring the primary key and falling back to a compound
// all-columns match (each NULL becomes `col IS NULL` rather than a bound
// param — a bound `= $n` against NULL never matches). Without a key the
// statement builders wrap this in `exactOneRow`, so it can only ever reach
// one row.
export function buildRowWhere(result, primaryKeyCols, rowIndex, engine, ph, castFor = () => '') {
  const row = result.rows[rowIndex];
  if (primaryKeyCols.length > 0) {
    // A binary key still addresses its row exactly — the tag binds as real
    // bytes. An undecodable one does not: we would be matching on a value we
    // never read, so refuse rather than guess at which row gets written.
    for (const c of primaryKeyCols) {
      const v = row[result.columns.indexOf(c)];
      if (isUndecoded(v)) {
        throw new Error(
          `Cannot address this row: primary key column "${c}" could not be decoded (${v.__undecoded}).`
        );
      }
    }
    return {
      clause: primaryKeyCols.map((c) => `${quoteIdentFor(engine, c)} = ${ph()}${castFor(c)}`).join(' AND '),
      params: primaryKeyCols.map((c) => row[result.columns.indexOf(c)]),
    };
  }
  // Clause and params are built in ONE pass: `ph()` must only advance for
  // columns that actually bind a parameter, or $n numbering drifts out of step
  // with the params array on postgres.
  const parts = [];
  const params = [];
  result.columns.forEach((c, i) => {
    const v = row[i];
    const ident = quoteIdentFor(engine, c);
    if (v === null) {
      parts.push(`${ident} IS NULL`);
    } else if (isUndecoded(v)) {
      // A value we never read cannot be matched, and `IS NOT NULL` in its
      // place made rows that differ only here look identical — the wrong one
      // could be written (owner, 30.09.2026: refuse, don't guess).
      throw new Error(
        `This table has no primary key and this row can't be identified exactly: column "${c}" could not be read (${v.__undecoded}). Add a primary key to edit it.`
      );
    } else {
      // Binary values bind as real bytes (as a binary primary key does), so
      // they match exactly instead of only "is not null".
      parts.push(`${ident} = ${ph()}${castFor(c)}`);
      params.push(v);
    }
  });
  return { clause: parts.join(' AND '), params };
}

/**
 * The WHERE + LIMIT that touch EXACTLY ONE row (owner, 30.09.2026: tables
 * without a primary key stay editable, DataGrip-style, but an edit can never
 * reach a second row). With a key the clause is already exact. Without one,
 * identical rows all match the all-columns clause, so:
 *   postgres  the one row's (tableoid, ctid) — tableoid for partitioned tables,
 *             where a ctid is only unique within a partition
 *   sqlite    its rowid (WITHOUT ROWID tables always have a primary key)
 *   mysql     no row id exists; LIMIT 1 on the statement itself
 * Identical duplicates are interchangeable, so "one of them" is exactly the
 * row the user changed.
 */
export function exactOneRow(engine, tableRef, clause, hasKey) {
  if (engine === 'mysql') return { where: clause, limit: ' LIMIT 1' };
  if (hasKey) return { where: clause, limit: '' };
  if (engine === 'postgres') {
    return { where: `(tableoid, ctid) = (SELECT tableoid, ctid FROM ${tableRef} WHERE ${clause} LIMIT 1)`, limit: '' };
  }
  return { where: `rowid = (SELECT rowid FROM ${tableRef} WHERE ${clause} LIMIT 1)`, limit: '' };
}

/**
 * One UPDATE per edited grid row. `editsByRow` maps rowIndex ->
 * [{ colIndex, value }] (the staged pendingEdits grouped by row).
 */
export function buildUpdateStatements({ engine, tableRef, result, primaryKeyCols, editsByRow, castFor = () => '' }) {
  return Object.entries(editsByRow).map(([rowIndexStr, edits]) => {
    const rowIndex = Number(rowIndexStr);
    const ph = makePlaceholder(engine); // one counter across SET + WHERE for $n engines
    const setClause = edits
      .map((e) => {
        const col = result.columns[e.colIndex];
        return `${quoteIdentFor(engine, col)} = ${ph()}${castFor(col)}`;
      })
      .join(', ');
    const { clause, params: whereParams } = buildRowWhere(result, primaryKeyCols, rowIndex, engine, ph, castFor);
    const { where, limit } = exactOneRow(engine, tableRef, clause, primaryKeyCols.length > 0);
    return {
      sql: `UPDATE ${tableRef} SET ${setClause} WHERE ${where}${limit}`,
      params: [...edits.map((e) => e.value), ...whereParams],
    };
  });
}

/**
 * One INSERT per staged pending row ([{ values: string[], omitCols?:
 * Set<string>|string[] }], one value per column). Blank cells insert NULL
 * (`$n::type` with a NULL param is still valid). `omitCols` names columns to
 * leave out of the column list entirely rather than bind as NULL — used by
 * duplicateRows for PK/unique columns, where an explicit NULL parameter
 * would override the column's DEFAULT (serial/uuid/etc.) and violate a NOT
 * NULL constraint; omitting the column lets the database apply its default.
 * One statement per row (rather than one multi-row INSERT) so each row can
 * carry its own omitted-column set — they still run atomically as part of
 * the same batch transaction.
 */
export function buildInsertStatement({ engine, tableRef, columns, rowsToSave, castFor = () => '' }) {
  return rowsToSave.map((r) => {
    const omit = r.omitCols instanceof Set ? r.omitCols : new Set(r.omitCols || []);
    const rowColumns = columns.filter((c) => !omit.has(c));
    const ph = makePlaceholder(engine);
    const colList = rowColumns.map((c) => quoteIdentFor(engine, c)).join(', ');
    const placeholders = rowColumns.map((c) => `${ph()}${castFor(c)}`).join(', ');
    const params = rowColumns.map((c) => {
      const v = r.values[columns.indexOf(c)];
      return v.trim() === '' ? null : v;
    });
    return {
      sql: `INSERT INTO ${tableRef} (${colList}) VALUES (${placeholders})`,
      params,
    };
  });
}

/** One DELETE per doomed grid row index (iterable of row indexes). */
export function buildDeleteStatements({ engine, tableRef, result, primaryKeyCols, rowIndexes, castFor = () => '' }) {
  return [...rowIndexes].map((rowIndex) => {
    const ph = makePlaceholder(engine);
    const { clause, params } = buildRowWhere(result, primaryKeyCols, rowIndex, engine, ph, castFor);
    const { where, limit } = exactOneRow(engine, tableRef, clause, primaryKeyCols.length > 0);
    return { sql: `DELETE FROM ${tableRef} WHERE ${where}${limit}`, params };
  });
}
